import React, { useCallback, useContext, useEffect, useState } from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import { defineMessages, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import Button from '@mui/material/Button';
import MuiCircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import AppContext from '../../../../context';
import CustomModal from '../../../../components/Modals/Modal';
import { ModalCloseBtn } from '../../../../components/statelessComponents/Buttons/ButtonsInModals';
import APPROVAL_STATUSES from '../../../../constants/approvalStepStatuses';
import { buildQuery, isEditor, isTeacher } from '../../../../helpers';
import getPaginationCurrentPageAfterRemoval from '../../../../helpers/getPaginationCurrentPageAfterRemoval';
import * as courseApprovalStepResultsActions from '../../../../actions/courseApprovalStepResults';
import PendingApprovalsTable from './PendingApprovalsTable';
import s from './CourseDetailsManager.scss';
const messages = defineMessages({
    actionNeeded: {
        id: 'CourseDetailsManager.actionNeeded',
        defaultMessage: 'Action needed',
    },
    pendingApprovalsCount: {
        id: 'CourseDetailsManager.pendingApprovalsCount',
        defaultMessage: 'Approval steps waiting for review: {count}',
    },
    viewPendingApprovals: {
        id: 'CourseDetailsManager.viewPendingApprovals',
        defaultMessage: 'View approvals',
    },
    approvalsLabel: {
        id: 'CourseDetailsManager.approvalsLabel',
        defaultMessage: 'Approvals',
    },
    teachingMaterials: {
        id: 'CourseDetailsManager.teachingMaterials',
        defaultMessage: 'Teaching materials',
    },
    noTeachingMaterials: {
        id: 'CourseDetailsManager.noTeachingMaterials',
        defaultMessage: 'No teaching materials have been added yet.',
    },
    loadError: {
        id: 'CourseDetailsManager.loadError',
        defaultMessage: 'Could not load pending approvals.',
    },
    updateError: {
        id: 'CourseDetailsManager.updateError',
        defaultMessage: 'Could not update approval status.',
    },
});
const getReviewApprovalUrl = (approval, status) => {
    const action = status === APPROVAL_STATUSES.APPROVED ? 'approve' : 'reject';
    const query = buildQuery({
        courseId: approval.courseId,
    });
    return `/api/approval-steps/${approval.approvalStepId}/approval-step-results/${approval.userId}/last/${action}?${query}`;
};
const getJson = async (res) => {
    const resText = await res.text();
    return resText ? JSON.parse(resText) : {};
};
function CourseDetailsManager({ auth, courseId, error, getPendingApprovalStepResults, getPendingApprovalStepResultsPendingTotal, intl: { formatMessage }, loading, pendingApprovals, pendingTotal, resetPendingApprovalStepResults, total, }) {
    const context = useContext(AppContext);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [perPage] = useState(50);
    const [statusFilter, setStatusFilter] = useState(APPROVAL_STATUSES.WAITING_FOR_REVIEW);
    const [updatingError, setUpdatingError] = useState('');
    const [updatingApprovalId, setUpdatingApprovalId] = useState('');
    const canManageCourse = isTeacher(auth) || isEditor(auth);
    const loadPendingApprovalsTotal = useCallback(() => {
        if (!courseId || !canManageCourse) {
            return;
        }
        getPendingApprovalStepResultsPendingTotal(courseId);
    }, [canManageCourse, courseId, getPendingApprovalStepResultsPendingTotal]);
    const loadPendingApprovals = useCallback(() => {
        if (!courseId || !canManageCourse) {
            return;
        }
        getPendingApprovalStepResults(courseId, {
            page: page + 1,
            perPage,
            status: statusFilter,
        });
    }, [
        canManageCourse,
        courseId,
        getPendingApprovalStepResults,
        page,
        perPage,
        statusFilter,
    ]);
    useEffect(() => {
        loadPendingApprovals();
    }, [loadPendingApprovals]);
    useEffect(() => {
        loadPendingApprovalsTotal();
    }, [loadPendingApprovalsTotal]);
    useEffect(() => () => {
        resetPendingApprovalStepResults();
    }, [resetPendingApprovalStepResults]);
    const reloadAfterReview = () => {
        const newPage = getPaginationCurrentPageAfterRemoval({
            currentPage: page,
            perPage,
            total,
        });
        if (newPage !== page) {
            setPage(newPage);
            return;
        }
        loadPendingApprovals();
    };
    const reviewApproval = async (approval, status) => {
        if (!context?.fetch) {
            return;
        }
        setUpdatingApprovalId(approval._id);
        setUpdatingError('');
        try {
            const res = await context.fetch(getReviewApprovalUrl(approval, status), {
                method: 'PUT',
            });
            const json = await getJson(res);
            if (!res.ok) {
                throw new Error(JSON.stringify(json));
            }
            loadPendingApprovalsTotal();
            reloadAfterReview();
        }
        catch (err) {
            console.error(err);
            setUpdatingError(formatMessage(messages.updateError));
        }
        finally {
            setUpdatingApprovalId('');
        }
    };
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    const handleStatusFilterChange = (status) => {
        setPage(0);
        setStatusFilter(status);
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setPage(0);
        setStatusFilter(APPROVAL_STATUSES.WAITING_FOR_REVIEW);
        loadPendingApprovalsTotal();
    };
    if (!canManageCourse) {
        return null;
    }
    return (React.createElement("div", { className: s.container },
        React.createElement(Paper, { className: s.section },
            React.createElement(Typography, { gutterBottom: true, variant: "h5" }, formatMessage(messages.actionNeeded)),
            React.createElement(Typography, { gutterBottom: true }, formatMessage(messages.pendingApprovalsCount, {
                count: pendingTotal,
            })),
            (error || updatingError) && (React.createElement(Typography, { color: "error", gutterBottom: true }, updatingError || formatMessage(messages.loadError))),
            React.createElement(Button, { "data-test-id": "9T3REmr-BFRxL8M-GeXx_", color: "primary", disabled: loading, onClick: () => setIsModalOpen(true), variant: "contained" },
                loading && (React.createElement(MuiCircularProgress, { color: "inherit", size: 18, className: s.buttonProgress })),
                formatMessage(messages.viewPendingApprovals))),
        React.createElement(Paper, { className: s.section },
            React.createElement(Typography, { gutterBottom: true, variant: "h5" }, formatMessage(messages.teachingMaterials)),
            React.createElement(Typography, null, formatMessage(messages.noTeachingMaterials))),
        React.createElement(CustomModal, { "aria-describedby": "pending-approvals-modal", isOpen: isModalOpen, onClose: handleCloseModal, width: "900px" },
            React.createElement("div", { id: "pending-approvals-modal" },
                React.createElement(Typography, { gutterBottom: true, variant: "h5" }, formatMessage(messages.approvalsLabel)),
                React.createElement(PendingApprovalsTable, { auth: auth, loading: loading, onChangePage: handleChangePage, onLoadPendingApprovals: loadPendingApprovals, onReviewApproval: reviewApproval, page: page, pendingApprovals: pendingApprovals, perPage: perPage, statusFilter: statusFilter, total: total, updatingApprovalId: updatingApprovalId, onStatusFilterChange: handleStatusFilterChange }),
                React.createElement(ModalCloseBtn, { "data-test-id": "pending-approvals-modal-close-btn", fitMobileLandscapeFullWidth: true, id: "pending-approvals-modal-close-btn", onClick: handleCloseModal })))));
}
export const mapStateToProps = (state) => ({
    auth: state.auth,
    error: state.courseApprovalStepResults.error,
    loading: state.courseApprovalStepResults.loading,
    pendingApprovals: state.courseApprovalStepResults.pendingApprovals,
    pendingTotal: state.courseApprovalStepResults.pendingTotal,
    total: state.courseApprovalStepResults.total,
});
export const mapDispatchToProps = (dispatch) => bindActionCreators({
    ...courseApprovalStepResultsActions,
}, dispatch);
export default compose(injectIntl, connect(mapStateToProps, mapDispatchToProps), withStyles(s))(CourseDetailsManager);
